/**
 * Cart Quantity Upsell – "Compra más y ahorra"
 *
 * Trabaja por VARIANTES de pack: cada opción cambia la línea del carrito a la variante
 * 2-pack / 3-pack del mismo producto (cantidad 1), en lugar de multiplicar la cantidad.
 * Los precios ya vienen renderizados desde Liquid (variant.price), no hay que simular nada.
 */

class CartQuantityUpsell extends HTMLElement {
  constructor() {
    super();
    this._updating = false;
  }

  connectedCallback() {
    this.key = this.dataset.key;
    this.line = parseInt(this.dataset.line);
    this.sectionId = this.dataset.sectionId;
    this.currentVariantId = this.dataset.currentVariantId;
    this.baseVariantId = this.dataset.baseVariantId;
    this.sellingPlanId = this.dataset.sellingPlanId || '';

    this.querySelectorAll('.cart-upsell__option').forEach((btn) => {
      btn.addEventListener('click', this._onOptionClick.bind(this));
    });
  }

  /* ──────────────────────────────────────
     Click en una opción de pack
     ────────────────────────────────────── */

  async _onOptionClick(e) {
    e.preventDefault();
    if (this._updating) return;

    const btn = e.currentTarget;
    const targetVid = btn.dataset.variantId;
    const isActive = btn.classList.contains('cart-upsell__option--active');

    // Si ya está activa esa variante, se vuelve al pack base (1 ud). Si no, se cambia a la elegida.
    const newVid = isActive ? this.baseVariantId : targetVid;
    if (!newVid || newVid === this.currentVariantId) return;

    await this._swapVariant(newVid);
  }

  /* ──────────────────────────────────────
     Cambiar la variante de la línea (quitar + añadir)
     ────────────────────────────────────── */

  async _swapVariant(variantId) {
    this._updating = true;
    this.classList.add('cart-upsell--loading');

    try {
      // 1) Añadir PRIMERO la variante elegida (preservando el plan de suscripción si lo había).
      //    Si el add falla (agotada, plan incompatible), la línea original sigue intacta.
      const body = { id: Number(variantId), quantity: 1 };
      if (this.sellingPlanId) body.selling_plan = Number(this.sellingPlanId);

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!addRes.ok) throw new Error(`Cart add failed: ${addRes.status}`);

      // 2) Quitar la línea original por su KEY, no por índice: tras el add los índices
      //    de línea se desplazan (lo recién añadido entra arriba).
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this.key, quantity: 0 }),
      });

      const cart = await (await fetch('/cart.js')).json();
      await this._refreshSection(cart);

      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { cart }, bubbles: true })
      );
    } catch (err) {
      console.error('[cart-quantity-upsell]', err);
      window.location.reload();
    } finally {
      this._updating = false;
      this.classList.remove('cart-upsell--loading');
    }
  }

  /* ──────────────────────────────────────
     Re-render de la sección del carrito
     ────────────────────────────────────── */

  async _refreshSection(cart) {
    if (!this.sectionId) {
      window.location.reload();
      return;
    }

    try {
      const response = await fetch(`/?sections=${this.sectionId}`);
      const data = await response.json();
      const html = new DOMParser().parseFromString(data[this.sectionId], 'text/html');

      const newContent = html.querySelector('cart-items-component');
      // La instancia correcta es la que contiene ESTE upsell (pagina /cart y drawer conviven).
      const currentContent = this.closest('cart-items-component') || document.querySelector('cart-items-component');

      if (newContent && currentContent) {
        currentContent.innerHTML = newContent.innerHTML;
        window.initCartReservation?.();
        window.initCartUpsells?.();
        window.updateUpsellTitle?.();
      }

      this._updateCartBubble(cart);

      if (cart.item_count === 0) {
        const dialog = document.querySelector('.cart-drawer__dialog');
        if (dialog) dialog.classList.add('cart-drawer--empty');
      }
    } catch (err) {
      console.error('[cart-quantity-upsell] Section refresh failed:', err);
      window.location.reload();
    }
  }

  _updateCartBubble(cart) {
    document.querySelectorAll('.cart-bubble__text-count').forEach((el) => {
      el.textContent = cart.item_count > 0 ? cart.item_count : '';
    });
  }
}

if (!customElements.get('cart-quantity-upsell')) {
  customElements.define('cart-quantity-upsell', CartQuantityUpsell);
}
